import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import {
  DEL_CONVERSATIONS,
  GET_CHAT_HISTORY,
  GET_USER_INF_FOR_WEB,
  LIST_CONVERSATION,
  LST_PBIMG,
  MARK_READS,
  MARK_READS_SELECTED,
  SEND_IMAGE_FROM_WEB,
  SEND_MESSAGE_FROM_WEB,
  SEND_VIDEO_FROM_WEB,
  TOTAL_UNREAD,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryKeys } from "~/lib/queryKeys";
import { useAuthStore } from "~/stores/useAuthStore";
import type { UserProfile } from "~/types/userProfile";
import type {
  ChatTab,
  ConversationListResponse,
  GetChatHistoryResponse,
  PartnerPublicImage,
  SendImageRequest,
  SendMessageRequest,
  SendMessageResponse,
  SendStickerRequest,
  SendVideoRequest,
  TotalUnreadResponse,
} from "./types";
import { toConversation } from "./utils/toConversation";

const CONVERSATION_TAKE = 15;

export const conversationListInfiniteQueryOptions = (tab: ChatTab) => {
  const conversatingOnly = tab === "active";
  const bookmarkOnly = tab === "bookmark";
  return infiniteQueryOptions({
    queryKey: queryKeys.chat.conversations(tab),
    queryFn: async ({ pageParam }) => {
      const items = await apiClient<ConversationListResponse>(
        LIST_CONVERSATION,
        {
          take: CONVERSATION_TAKE,
          time_stamp: pageParam ?? null,
          conversating_only: conversatingOnly,
          bookmark_only: bookmarkOnly,
        },
      );
      return items.map(toConversation);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.length === 0) return undefined;
      // 初回ページ: take 未満なら全件取得済み
      if (lastPageParam == null && lastPage.length < CONVERSATION_TAKE)
        return undefined;
      // 末尾から有効な lastMessageAt を探す
      let nextCursor: string | undefined;
      for (let i = lastPage.length - 1; i >= 0; i--) {
        const at = lastPage[i]?.lastMessageAt;
        if (at) {
          nextCursor = formatTimestampCursor(at);
          break;
        }
      }
      if (!nextCursor) return undefined;
      // カーソルが進んでいなければ終端
      if (nextCursor === lastPageParam) return undefined;
      return nextCursor;
    },
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/** ISO 文字列を yyyyMMddHHmmss カーソル形式 (UTC) に変換 */
const formatTimestampCursor = (isoString: string): string => {
  const d = new Date(isoString);
  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, "0"),
    String(d.getUTCDate()).padStart(2, "0"),
    String(d.getUTCHours()).padStart(2, "0"),
    String(d.getUTCMinutes()).padStart(2, "0"),
    String(d.getUTCSeconds()).padStart(2, "0"),
  ].join("");
};

/** 全体未読数を取得する専用 API（token のみ。body 不要） */
export const totalUnreadQueryOptions = () => {
  return queryOptions({
    queryKey: queryKeys.chat.totalUnread(),
    queryFn: () => apiClient<TotalUnreadResponse>(TOTAL_UNREAD),
    staleTime: 0,
  });
};

export const chatHistoryQueryOptions = (frdId: string) => {
  return queryOptions({
    queryKey: queryKeys.chat.messages(frdId),
    queryFn: () =>
      apiClient<GetChatHistoryResponse>(GET_CHAT_HISTORY, {
        frd_id: frdId,
        time_stamp: "",
        take: 20,
      }),
    staleTime: 0,
    refetchInterval: 1_000,
  });
};

export const sendMessageRequest = async (
  req: SendMessageRequest,
): Promise<SendMessageResponse> => {
  return apiClient<SendMessageResponse>(SEND_MESSAGE_FROM_WEB, {
    user_id: req.userId,
    rcv_id: req.rcvId,
    content: req.content,
    is_free: req.isFree,
  });
};

export const sendStickerRequest = async (
  req: SendStickerRequest,
): Promise<SendMessageResponse> => {
  return apiClient<SendMessageResponse>(SEND_MESSAGE_FROM_WEB, {
    user_id: req.userId,
    rcv_id: req.rcvId,
    content: req.content,
    is_sticker: true,
    is_gift: req.isGift,
  });
};

export const markReadsRequest = async (frdId: string): Promise<void> => {
  try {
    await apiClient<unknown>(MARK_READS, {
      frd_id: [frdId],
    });
  } catch {
    // fire-and-forget: エラーは無視する
  }
};

export const markAllReadsRequest = async (
  userId: string,
  frdIds: string[],
): Promise<void> => {
  await apiClient<unknown>(MARK_READS, {
    user_id: userId,
    frd_id: frdIds,
  });
};

/** 選択した相手だけを per-partner で既読化する（全件既読パスは踏まない） */
export const markReadsSelectedRequest = async (
  userId: string,
  partnerIds: string[],
): Promise<void> => {
  await apiClient<unknown>(MARK_READS_SELECTED, {
    user_id: userId,
    frd_id: partnerIds,
  });
};

export const deleteAllConversationsRequest = async (
  userId: string,
): Promise<void> => {
  await apiClient<unknown>(DEL_CONVERSATIONS, {
    user_id: userId,
    frd_id: [],
    isRequestDeleteAll: true,
  });
};

/** 指定した相手との会話のみ削除する（選択削除） */
export const deleteSelectedConversationsRequest = async (
  userId: string,
  partnerIds: string[],
): Promise<void> => {
  await apiClient<unknown>(DEL_CONVERSATIONS, {
    user_id: userId,
    frd_id: partnerIds,
    isRequestDeleteAll: false,
  });
};

export const sendImageRequest = async (
  req: SendImageRequest,
): Promise<void> => {
  await apiClient<unknown>(SEND_IMAGE_FROM_WEB, {
    userId: req.userId,
    partnerId: req.partnerId,
    fileId: req.fileId,
  });
};

export const sendVideoRequest = async (
  req: SendVideoRequest,
): Promise<void> => {
  await apiClient<unknown>(SEND_VIDEO_FROM_WEB, {
    userId: req.userId,
    partnerId: req.partnerId,
    fileId: req.fileId,
    duration: req.duration,
  });
};

/** STF サーバーに画像をアップロードして file_id を返す */
export const uploadImageToStf = async (
  file: File,
  userId: string,
): Promise<string> => {
  const token = useAuthStore.getState().token;
  if (!token) throw new Error("Not authenticated");
  const stfUrl = import.meta.env.DEV ? "/stf" : import.meta.env.VITE_STF_URL;
  const url = `${stfUrl}/api=upl_img_for_utage_web&img_cat=0&user_id=${userId}&token=${token}`;
  const res = await fetch(url, { method: "POST", body: file });
  if (!res.ok) throw new Error("Image upload failed");
  const json = await res.json();
  const fileId: unknown =
    json?.data?.image_id ?? json?.data?.file_id ?? json?.image_id;
  if (!fileId || typeof fileId !== "string") throw new Error("No file_id");
  return fileId;
};

/** STF サーバーに動画をアップロードして { fileId, duration } を返す */
export const uploadVideoToStf = async (
  file: File,
): Promise<{ fileId: string; duration: number }> => {
  const token = useAuthStore.getState().token;
  if (!token) throw new Error("Not authenticated");
  const stfUrl = import.meta.env.DEV ? "/stf" : import.meta.env.VITE_STF_URL;
  const url = `${stfUrl}/api=upl_file&token=${token}`;
  const res = await fetch(url, { method: "POST", body: file });
  if (!res.ok) throw new Error("Video upload failed");
  const json = await res.json();
  const fileId: unknown = json?.data?.file_id ?? json?.file_id;
  if (!fileId || typeof fileId !== "string") throw new Error("No file_id");
  const duration = await getVideoDuration(file);
  return { fileId, duration };
};

/** メッセージ相手のプロフィール（簡易表示用） */
export const partnerProfileQueryOptions = (userId: string) => {
  return queryOptions({
    queryKey: queryKeys.user.profile(userId),
    queryFn: () =>
      apiClient<UserProfile>(GET_USER_INF_FOR_WEB, {
        user_id: useAuthStore.getState().userId,
        req_user_id: userId,
      }),
  });
};

/** メッセージ相手の公開画像一覧（簡易表示用） */
export const partnerPublicImagesQueryOptions = (userId: string) => {
  return queryOptions({
    queryKey: queryKeys.user.publicImages(userId),
    queryFn: () =>
      apiClient<PartnerPublicImage[]>(LST_PBIMG, {
        req_user_id: userId,
        skip: 0,
        take: 50,
      }),
  });
};

const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    video.src = url;
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Math.round(video.duration));
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
  });
};
